import React, { useRef, useCallback, useState, useEffect } from 'react';
import { DownloadIcon, ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';

interface ReportingTableProps {
  columns: string[];
  rows: string[][];
  dark: boolean;
}

const LINK_COLUMN_NAMES = ['REPORT_LINK', 'report_link'];
const PAGE_SIZE = 20;

const isLinkColumn = (col: string) =>
  LINK_COLUMN_NAMES.some(name => col.toUpperCase() === name.toUpperCase());

const isSafeUrl = (url: string) => /^https?:\/\//i.test(url);

const getPageNumbers = (current: number, total: number): (number | '…')[] => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '…')[] = [1];
  if (current > 3) pages.push('…');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) pages.push(p);
  if (current < total - 2) pages.push('…');
  pages.push(total);
  return pages;
};

const ReportingTable: React.FC<ReportingTableProps> = React.memo(({ columns, rows, dark }) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const isPaginated = rows.length > PAGE_SIZE;
  const totalPages = Math.ceil(rows.length / PAGE_SIZE);
  const pagedRows = isPaginated
    ? rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
    : rows;

  useEffect(() => {
    setCurrentPage(1);
  }, [rows]);

  const handleDownloadCSV = useCallback(() => {
    const escape = (text: string) => {
      const safeText = /^[=+\-@]/.test(text) ? `'${text}` : text;
      return safeText.includes(',') || safeText.includes('"') || safeText.includes('\n')
        ? `"${safeText.replace(/"/g, '""')}"`
        : safeText;
    };

    const csvRows = [
      columns.map(escape).join(','),
      ...rows.map(row => columns.map((_, ci) => escape(row?.[ci] ?? '')).join(',')),
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `reporting_table_${Date.now()}.csv`;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [columns, rows]);

  if (!columns.length || !rows.length) return null;

  const borderColor = dark ? '#444' : '#d1d5db';
  const rowBorderColor = dark ? '#444' : '#e5e7eb';

  return (
    <div style={{ marginBottom: '1.5em', marginTop: '1em' }}>
      {isPaginated && (
        <div style={{ fontSize: '0.8em', color: dark ? '#aaa' : '#6b7280', marginBottom: '0.4em', textAlign: 'right' }}>
          Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, rows.length)} of {rows.length} items
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table
          ref={tableRef}
          style={{
            borderCollapse: 'collapse',
            width: '100%',
            border: `1px solid ${borderColor}`,
            fontSize: '0.9em',
          }}
        >
          <thead
            style={{
              backgroundColor: 'var(--form-accent)',
              borderBottom: `2px solid ${dark ? '#555' : '#9ca3af'}`,
            }}
          >
            <tr>
              {columns.map((col, i) => (
                <th
                  scope="col"
                  key={i}
                  style={{
                    padding: '0.75em 1em',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    backgroundColor: 'transparent',
                    borderRight: `1px solid ${borderColor}`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((row, ri) => (
              <tr
                key={ri}
                style={{
                  borderBottom: `1px solid ${rowBorderColor}`,
                }}
              >
                {columns.map((_, ci) => { const cell = row[ci];
                  const colName = columns[ci] ?? '';
                  const isLink = isLinkColumn(colName);
                  const cellValue = cell ?? '';
                  return (
                    <td
                      key={ci}
                      style={{
                        padding: '0.75em 1em',
                        backgroundColor: 'transparent',
                        borderRight: `1px solid ${rowBorderColor}`,
                      }}
                    >
                      {isLink && cellValue && isSafeUrl(cellValue) ? (
                        <a
                          href={cellValue}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#2664D3', textDecoration: 'underline' }}
                        >
                          Open
                        </a>
                      ) : (
                        cellValue
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5em' }}>
        {isPaginated ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em', fontSize: '0.85em' }}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '0.3em 0.5em',
                backgroundColor: 'var(--color-secondary)',
                color: 'var(--color-text)',
                border: `1px solid ${borderColor}`,
                borderRadius: '0.375rem',
                cursor: currentPage === 1 ? 'default' : 'pointer',
                opacity: currentPage === 1 ? 0.4 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                fontFamily: 'inherit',
              }}
              aria-label="Previous page"
            >
              <ChevronLeftIcon width={14} height={14} />
            </button>
            {getPageNumbers(currentPage, totalPages).map((p, i) =>
              p === '…' ? (
                <span key={`ellipsis-${i}`} style={{ padding: '0 0.2em', color: dark ? '#aaa' : '#6b7280', userSelect: 'none' }}>{'…'}</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  style={{
                    minWidth: '2em',
                    padding: '0.3em 0.4em',
                    backgroundColor: p === currentPage ? 'var(--form-accent)' : 'var(--color-secondary)',
                    color: 'var(--color-text)',
                    border: `1px solid ${p === currentPage ? (dark ? '#666' : '#9ca3af') : borderColor}`,
                    borderRadius: '0.375rem',
                    cursor: p === currentPage ? 'default' : 'pointer',
                    fontWeight: p === currentPage ? 'bold' : 'normal',
                    fontFamily: 'inherit',
                    fontSize: '0.9em',
                  }}
                  aria-label={`Page ${p}`}
                  aria-current={p === currentPage ? 'page' : undefined}
                >
                  {p}
                </button>
              )
            )}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '0.3em 0.5em',
                backgroundColor: 'var(--color-secondary)',
                color: 'var(--color-text)',
                border: `1px solid ${borderColor}`,
                borderRadius: '0.375rem',
                cursor: currentPage === totalPages ? 'default' : 'pointer',
                opacity: currentPage === totalPages ? 0.4 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                fontFamily: 'inherit',
              }}
              aria-label="Next page"
            >
              <ChevronRightIcon width={14} height={14} />
            </button>
          </div>
        ) : (
          <div />
        )}
        <button
          onClick={handleDownloadCSV}
          style={{
            padding: '0.5em',
            fontSize: '0.85em',
            backgroundColor: 'var(--color-secondary)',
            color: 'var(--color-text)',
            border: `1px solid ${borderColor}`,
            borderRadius: '0.375rem',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s, border-color 0.2s',
            fontFamily: 'inherit',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = dark ? '#424248' : '#e5e7eb';
            e.currentTarget.style.borderColor = dark ? '#555' : '#9ca3af';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-secondary)';
            e.currentTarget.style.borderColor = dark ? '#444' : '#d1d5db';
          }}
          title="Download table as CSV"
          aria-label="Download table as CSV"
        >
          <DownloadIcon width={16} height={16} style={{ display: 'block' }} />
        </button>
      </div>
    </div>
  );
});

ReportingTable.displayName = 'ReportingTable';

export default ReportingTable;
